import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { filterDateRangesByNights } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/utils'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import { parseAsBoolean, useQueryState } from 'nuqs'
import type { OfferDurationOption } from '../DurationTabs/OfferDurationTabs'
import type { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import { fareClassToUrlMap, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import moment from 'moment'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import MaldivesCharterBuyPointsBanner from 'components/Flights/CharterBundle/MaldivesCharterBuyPointsBanner'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { MaldivesCharterCalendarModalBody } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesCharterCalendarModalLayout'

export interface CharterTripOption {
  checkIn: string
  checkOut: string
  departDate: string
  returnDate: string
}

interface Props {
  offer: App.HotelOffer
  occupancies: Array<App.Occupants>
  duration: number
  durationOptions: Array<OfferDurationOption>
  priceMode: OfferPagePriceMode
  onPriceModeChange?: (priceMode: OfferPagePriceMode) => void
  fareClass: FareClassWithReward
  charterBundleRoute: App.CharterBundleRoute
  checkInDate?: string
  onDurationChange: (nights: number) => void
  onClose?: () => void
  onBack?: () => void
  onDateSelected: (option: CharterTripOption) => void
  onContinue?: () => void
  onResetDates?: () => void
  onFareClassChange: (fareClass: FareClassWithReward) => void
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
  disableContinue?: boolean
  /**
   * Forwarded to the calendar's flight search hooks so calendar tiles share a Redux
   * cache key with feeder-aware callers and feeder-priced fares populate calendar pricing.
   */
  feederOriginAirportCode?: string
}

function CharterCalendarFilter(props: Props) {
  const intl = useIntl()
  const {
    offer,
    occupancies,
    duration,
    durationOptions,
    priceMode,
    onPriceModeChange,
    fareClass,
    charterBundleRoute,
    checkInDate,
    onDurationChange,
    onClose,
    onBack,
    onDateSelected,
    onContinue,
    onResetDates,
    onFareClassChange,
    scrollContainerRef,
    disableContinue,
    feederOriginAirportCode,
  } = props
  const isRewardSeatsEnabled = useRewardSeatsEnabled()

  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState('luxPlus', parseAsBoolean.withDefault(true))
  const { fareClassFromUrl, setFareClassUrl, cabinFilter, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly } = useCharterFilterUrlState()
  const selectedFareClass = fareClassFromUrl ?? fareClass
  const fareClassForSearch = toBaseFareClass(selectedFareClass)
  const [selectedMonth, setSelectedMonth] = useState(checkInDate ? moment(checkInDate).format(ISO_DATE_SHORT_FORMAT) : moment().format(ISO_DATE_SHORT_FORMAT))

  const { dateRangesByMonth: allDateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies,
    fareClass: fareClassForSearch,
    disabled: occupancies.length === 0,
    feederOriginAirportCode,
  })

  const dateRangesByMonth = useMemo(
    () => filterDateRangesByNights(allDateRangesByMonth, duration),
    [allDateRangesByMonth, duration],
  )

  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms: occupancies,
    fareClass: fareClassForSearch,
    dateRangesByMonth,
    charterBundleRoute,
    feederOriginAirportCode,
  })

  const selectedOptionId = useMemo(() => checkInDate ? `${checkInDate}-${duration}n` : undefined, [checkInDate, duration])

  const durationDropdownOptions = useMemo(() => {
    // Get valid durations for charter flights (e.g., 5 and 12 nights for Maldives)
    const validCharterDurations = CHARTER_FLIGHT_DETAILS[charterBundleRoute]?.validDurations ?? []
    // Only include duration options that are valid charter durations and have available (non-sold-out) packages
    return durationOptions
      .filter(option => !option.soldOut && validCharterDurations.includes(option.duration))
      .map(option => ({
        value: option.duration,
        label: intl.formatMessage(
          {
            id: 'JiMJ6iF78MMwdhhRYnVi',
            defaultMessage: '{count, plural, one {# night} other {# nights}} & flights',
            description: 'Duration option label showing night count with flights for charter calendar',
          },
          { count: option.duration },
        ),
      }))
  }, [intl, durationOptions, charterBundleRoute])
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, occupancies)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)

  // Sync fare class to URL so reward selections persist
  const setFareClassWithUrl = useCallback((fc: FareClassWithReward | null) => {
    if (fc) {
      onFareClassChange(fc)
      setFareClassUrl(fareClassToUrlMap[fc])
    }
  }, [onFareClassChange, setFareClassUrl])

  const actionsElement = (
    <CharterBundleCalendarActions
      nights={duration}
      onDurationChange={onDurationChange}
      durationOptions={durationDropdownOptions}
      luxPlusEnabled={luxPlusEnabled}
      onLuxPlusToggle={setLuxPlusEnabled}
      cabinFilter={cabinFilter}
      onCabinFilterChange={setCabinFilter}
      availableFareClasses={allowedFareClassesForModal}
      rewardSeatsOnly={rewardSeatsOnly}
      onRewardSeatsOnlyChange={setRewardSeatsOnly}
      priceMode={priceMode}
      onPriceModeChange={onPriceModeChange}
    />
  )

  const handleDateSelect = (option: CharterTripOption & { fareClass?: FareClassWithReward }) => {
    const selectedCabin = option.fareClass ?? fareClassFromUrl ?? fareClass
    if (selectedCabin) {
      setFareClassWithUrl(selectedCabin)
    }
    onDateSelected(option)
    setSelectedMonth(moment(option.checkIn).format(ISO_DATE_SHORT_FORMAT))
  }

  const secondaryActionProps = onResetDates ? {
    children: intl.formatMessage({
      id: 'AVgf6troQ3hI4oK1NpoU',
      defaultMessage: 'Reset',
      description: 'Button to reset selected dates in charter calendar filter',
    }),
    onClick: onResetDates,
    type: 'button' as const,
  } : undefined

  const primaryActionProps = onContinue ? {
    children: intl.formatMessage({
      id: '2xvN9YEDXgp4zyMRJSS4',
      defaultMessage: 'Continue',
      description: 'Button to continue after selecting dates in charter calendar filter',
    }),
    onClick: onContinue,
    type: 'button' as const,
    disabled: disableContinue,
  } : undefined

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: 'd4FPk4_HrNeg5pvjmCBF',
          defaultMessage: 'Select your trip dates',
          description: 'Modal header title for charter flight calendar date selection',
        })}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      />
      <MaldivesCharterCalendarModalBody ref={scrollContainerRef}>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={occupancies}
            luxPlusEnabled={luxPlusEnabled}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClassForSearch}
            selectedFareClass={selectedFareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="search"
            offer={offer}
            cabinFilter={cabinFilter}
            rewardSeatsOnly={rewardSeatsOnly}
            onFareClassChange={setFareClassWithUrl}
            headerActions={actionsElement}
            stickyHeaderBanner={isRewardSeatsEnabled ? <MaldivesCharterBuyPointsBanner flush /> : undefined}
            scrollContainerRef={scrollContainerRef}
            feederOriginAirportCode={feederOriginAirportCode}
          />
        </ModalContent>
      </MaldivesCharterCalendarModalBody>
      <ModalFooter
        secondaryActionProps={secondaryActionProps}
        primaryActionProps={primaryActionProps}
      />
    </>
  )
}

export default CharterCalendarFilter
